/**
 * @fileoverview Supabase client bootstrap for the web runtime.
 *
 * Supabase is entirely optional. When the required environment variables are
 * absent (e.g. desktop Electron build, or a local web build without a backend),
 * {@link getSupabase} returns `null` and the app falls back to local-only
 * persistence with no auth. This keeps parity with the desktop experience.
 *
 * Required env vars (Vite, exposed to the browser):
 *  - VITE_SUPABASE_URL
 *  - VITE_SUPABASE_ANON_KEY
 */

import { createClient, type SupabaseClient } from '@supabase/supabase-js'

const url = import.meta.env.VITE_SUPABASE_URL as string | undefined
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined

/** True when both Supabase env vars are present and non-empty. */
export const isSupabaseConfigured = Boolean(url && anonKey)

let client: SupabaseClient | null = null

/**
 * Return the singleton Supabase client, or `null` when not configured.
 * Safe to call from any runtime; never throws.
 */
export function getSupabase(): SupabaseClient | null {
  if (!isSupabaseConfigured) return null
  if (!client) {
    client = createClient(url as string, anonKey as string, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true
      }
    })
  }
  return client
}
